import { ShelfSet } from '@/Engine/Wardrobe/ShelfSet';
import { PerforatedPanel } from '@/Engine/Wardrobe/PerforatedPanel';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { railGroup, wardrobeItem, wardrobeModule, xyz } from '@/Interfaces/common';
import { V3dTools } from '@/v3d/common';
import { Part } from '@/Engine/Wardrobe/Part';
import { Group, Line3, Vector3 } from 'three';
import { Wardrobe } from '@/Engine/Wardrobe/Wardrobe';
import { railShift } from '@/Constants/common';
import { convertItem, findRail } from '@/lib/createModule';
import { IV3D } from '@/Interfaces/v3d';
import { getOptimalRailSet } from '@/lib/getRailWidths';
import { useStoreData } from '@/Store/storeData';

declare let v3d: IV3D;
interface railGroupProps extends railGroup {
    // onChangePosition: (uuid: string, position: xyz)=>void;
    v3dTools: V3dTools;
    parent: Wardrobe;
}
export class RailGroup extends WardrobeObject {
    rails: Array<Part> = [];
    v3dTools: V3dTools;
    templates: Array<wardrobeItem>;
    parent: Wardrobe;
    _model: Group = new Group();

    get raw(): railGroup {
        const raw = JSON.parse(JSON.stringify(
            {
                uuid: this.uuid,
                templates: this.templates,
                width: this.width,
                height: this.height,
                depth: this.depth,
                position: this.position.toArray() as xyz,
                color: this.color,
                side: this.side,
            },
        )) as railGroup;

        const linesCount = this.shelvesLinesCount;
        const addCovers = useStoreData().addBracketCovers;

        if (addCovers && linesCount > 0) {
            const accessoryItem = useStoreData().items.find(item => item.name.includes('GPL-40') && item.color.hex === this.color);
            if (accessoryItem) {
                raw.assets = [];
                for (let i = 0; i < linesCount; i++) {
                    raw.assets.push(convertItem(accessoryItem));
                }
            }
        }

        return raw;
    }

    get racks() {
        return this.parent.getModulesByRailUUID(this.uuid);
    }

    get shelvesLinesCount(): number {
        const shelvesByPlace = new Map<number, Array<ShelfSet | PerforatedPanel>>();
        this.racks.forEach((rack) => {
            const shelves = this.parent.getShelvesByModuleUUID(rack.uuid);
            shelves.forEach((shelf) => {
                if (!shelvesByPlace.has(shelf.place)) {
                    shelvesByPlace.set(shelf.place, []);
                }
                shelvesByPlace.get(shelf.place).push(shelf);
            });
        });

        let count = 0;
        shelvesByPlace.forEach((shelves) => {
            // не считаем для полок глубиной 50
            const hasDepth50 = shelves.some((shelf) => shelf.compatibilityDepth === '50');

            if (hasDepth50) {
                return;
            }

            // не считаем если есть слева и справа аксессуар
            const hasLeftExtra = shelves.some((shelf) => !!shelf.templates.bracketLeftExtra);
            const hasRightExtra = shelves.some((shelf) => !!shelf.templates.bracketRightExtra);
            if (hasLeftExtra && hasRightExtra) {
                return;
            }
            count++;
        });

        return count;
    }
    constructor({ uuid, width, height, depth, v3dTools,
                    color, side, templates, parent }: railGroupProps) {
        super({ uuid, width, height, depth, raycastBox: false, type: 'railGroup',
            color, side });
        this.parent = parent;
        this.v3dTools = v3dTools;
        this.model.userData.side = side;
        this.model.add(this._model);
        this.side = side;
        this.templates = templates;
        this.update();
    }

    clear() {
        let i = this.rails.length;
        while (i--) {
            if (this.rails[i].destroy) {
                this.rails[i].destroy();
            } else {
                this.rails[i].model.removeFromParent();
            }
            this.rails.splice(i, 1);
        }
    }

    getRailLine() {
        const leftPoints: Array<Vector3> = [];
        const rightPoints: Array<Vector3> = [];
        // const distances: Array<number> = [];
        const lines: Array<Line3> = [];
        this.racks.forEach((rack)=>{
            leftPoints.push(rack.upLeftPoint);
            rightPoints.push(rack.upRightPoint);
        });
        leftPoints.forEach((leftPoint)=>{
            rightPoints.forEach((rightPoint)=>{
                lines.push(new Line3(leftPoint, rightPoint));
            });
        });
        lines.sort((line1, line2)=>line2.distance()-line1.distance());
        return lines[0];
    }

    getStartPoint(): Vector3 | null {
        const rackPositions: Array<Vector3> = [];
        const lines: Array<Line3> = [];
        this.racks.forEach((rack)=>{
            rackPositions.push(rack.upLeftPoint);
        });
        rackPositions.forEach((position1)=>{
            rackPositions.forEach((position2)=> {
                if (position2.distanceTo(position1) < 0.0001) return;
                lines.push(new Line3(position1, position2));
            });
        });
        lines.sort((line1, line2)=>line2.distance()-line1.distance());

        if (!lines[0]) return null;
        return (lines[0].start.x<lines[0].end.x?lines[0].start:lines[0].end);
    }

    update() {
        // const maxDistance = Math.max(... lines.map((line)=>line.distance()));
        this._update(this.getRailLine());
    }

    _update(line: Line3) {
        if (!line) return;
        const distance = line.distance();
        const error = 0.001;
        // TODO надо бы эту проверку добавить, но она не должна в конструкторе срабатывать
        // if ((distance - this.width) < error) return;
        this.clear();
        this.width = distance;
        // const oldTemplate = this.templates[0];
        // const rail = findRail(width, this.color);
        // let countL = Math.floor((this.width-0.95)/1.25);
        // countL = countL<0?0:countL;
        this.templates = [];
        const isGroup = this.racks.length > 1;
        // TODO передавать из доступных рельсов
        if (!isGroup) {
            if (this.racks[0] && this.racks[0].shelves[0].compatibilityWidth)
                this.templates.push(findRail(this.racks[0].shelves[0].compatibilityWidth, this.color));
        }

        if (!this.templates.length)
            getOptimalRailSet((this.width+railShift + 0.011)*100, [ 95, 65, 126 ]).forEach((width)=> {
                    this.templates.push(findRail(String(width), this.color));
                },
            );
        // this.templates.length = countL;
        // const railTemplate = findRail('120', this.color);
        // this.templates.fill(railTemplate);
        // let lastWidth = this.width - countL*1.25;
        // // const countM = Math.ceil(lastWidth/0.95);
        // // const countS = Math.ceil(lastWidth/0.65);
        //
        // const fillRail = () => {
        //     if (lastWidth > 0.95) {
        //         const railTemplate = findRail('120', this.color);
        //         this.templates.push(railTemplate);
        //         lastWidth -= 1.25;
        //     } else
        //     if (lastWidth > 0.65 && lastWidth < 0.95) {
        //         const railTemplate = findRail('90', this.color);
        //         this.templates.push(railTemplate);
        //         lastWidth -= 0.95;
        //     } else
        //     {
        //         const railTemplate = findRail('60', this.color);
        //         this.templates.push(railTemplate);
        //         lastWidth -= 0.65;
        //     }
        // };
        //
        // while (lastWidth > 0) {
        //     fillRail();
        // }
        this.updatePosition();
        this._updateModels(line);
    }

    _updateModels(line: Line3) {
        let shift = 0;
        const isGroup = this.racks.length > 1;
        // console.log(this.templates);
        this.templates.forEach((rail)=>{
            const railObj = this.v3dTools.getObjectByName(rail?.name);
            if (railObj) {
                const railPart = new Part({
                    ...rail,
                    model: railObj,
                    uuid: '',
                });
                railPart.setColor(this.color);
                // console.log(isGroup);
                const shiftVector = new Vector3(shift + railPart.width/2, 0, 0);
                // const ray = new Ray(line.start);
                // ray.lookAt(line.end);
                // ray.at(shift,shiftVector);
                // rail.position = shiftVector;
                // console.log(railPart.position.clone());
                // console.log(shift,isGroup,railPart.width/2,railShift);
                railPart.position = railPart.position.clone().add(shiftVector);
                shift+=railPart.width;
                this.rails.push(railPart);
                this._model.add(railPart.model);
            }
        });
    }

    updatePosition() {
        const start = this.getStartPoint();
        const shiftVector = new Vector3(railShift,0,0);
        if (start) {
            this.position = start.clone().sub(shiftVector);
        } else if (this.racks.length) {
            let minPoint: Vector3 | null = null;
            this.racks.forEach((rack)=>{
                if (!minPoint || minPoint.x > rack.upLeftPoint.x)
                    minPoint = rack.upLeftPoint.clone();
            });
            if (minPoint)
                this.position = minPoint.sub(shiftVector);
            return;
        }

    }

    setColor(hex: string) {
        this.color = hex.replace('#','');
        const color = hex.startsWith('#')?hex:'#'+hex;
        this.rails.forEach((item)=>{
            item.setColor(color);
        });
    }

    destroy() {
        this.clear();
        this.model.clear();
        this.model.removeFromParent();
    }
}
